/**
 * Admin Authentication Service
 *
 * Handles authentication specifically for admin users with role-based access control
 */

import { apiService } from './api'
import { clearStoredSession, ADMIN_HOSTNAME } from './sessionStorageKeys'

export interface AdminUser {
  id: string
  email: string
  name: string
  role: 'admin'
  permissions: string[]
  createdAt: string
}

export interface AdminAuthResponse {
  user: AdminUser
  token: string
  expiresIn: number
}

export interface AdminLoginData {
  email: string
  password: string
}

export interface TwoFactorChallenge {
  requiresTwoFactor?: boolean
  challengeId?: string
  expiresInSec?: number
  retryAfterSec?: number
}

/** 1단계 결과 — 토큰을 받았거나, 코드 입력이 남았거나 */
export type AdminLoginResult =
  | ({ requiresTwoFactor: true } & TwoFactorChallenge)
  | ({ requiresTwoFactor: false } & AdminAuthResponse)

class AdminAuthService {
  private adminToken: string | null = null
  private adminUser: AdminUser | null = null

  constructor() {
    this.loadAdminFromStorage()
  }

  private loadAdminFromStorage(): void {
    try {
      this.adminToken = localStorage.getItem('admin_token')
      const userData = localStorage.getItem('admin_user')
      if (userData) {
        this.adminUser = JSON.parse(userData)
      }
    } catch (error) {
      console.warn('Failed to load admin data from storage:', error)
      this.clearAdminData()
    }
  }

  private storeAdminData(user: AdminUser, token: string): void {
    try {
      this.adminToken = token
      this.adminUser = user
      localStorage.setItem('admin_token', token)
      localStorage.setItem('admin_user', JSON.stringify(user))
    } catch (error) {
      console.warn('Failed to store admin data:', error)
    }
  }

  private clearAdminData(): void {
    this.adminToken = null
    this.adminUser = null
    // 지울 키 목록은 sessionStorageKeys 한 곳에서 정한다
    clearStoredSession(ADMIN_HOSTNAME)
  }

  /**
   * 1단계 — 비밀번호.
   *
   * 2차 인증이 켜져 있으면 토큰 대신 challenge 를 받는다. 토큰은
   * `verifyOtp()` 를 통과해야 나온다.
   */
  async login(data: AdminLoginData): Promise<AdminLoginResult> {
    try {
      const response = await apiService.client.post<{
        success: boolean
        data: AdminAuthResponse & TwoFactorChallenge
      }>('/admin/login', data)

      if (!response.data.success || !response.data.data) {
        throw new Error('Login failed')
      }

      const payload = response.data.data

      if (payload.requiresTwoFactor) {
        // 아직 로그인된 것이 아니다. 저장할 것이 없다.
        return {
          requiresTwoFactor: true,
          challengeId: payload.challengeId,
          expiresInSec: payload.expiresInSec,
          retryAfterSec: payload.retryAfterSec,
        }
      }

      return this.acceptSession(payload)
    } catch (error: any) {
      this.clearAdminData()
      throw error
    }
  }

  /** 2단계 — 코드 확인. 통과하면 그때 토큰이 저장된다. */
  async verifyOtp(
    challengeId: string,
    code: string
  ): Promise<AdminLoginResult> {
    try {
      const response = await apiService.client.post<{
        success: boolean
        data: AdminAuthResponse
      }>('/admin/login/verify', { challengeId, code })

      if (!response.data.success || !response.data.data) {
        throw new Error('Login failed')
      }

      return this.acceptSession(response.data.data)
    } catch (error: any) {
      this.clearAdminData()
      throw error
    }
  }

  /** 코드를 다시 보낸다. 실패해도 OTP 화면은 유지된다. */
  async resendOtp(challengeId: string): Promise<TwoFactorChallenge> {
    const response = await apiService.client.post<{
      success: boolean
      data: TwoFactorChallenge
    }>('/admin/login/resend', { challengeId })
    return response.data.data
  }

  /** 토큰을 받아 저장한다 */
  private acceptSession(payload: AdminAuthResponse): AdminLoginResult {
    const { user, token } = payload

    // Verify user has admin role
    if (user.role !== 'admin') {
      throw new Error('Access denied: Admin privileges required')
    }

    this.storeAdminData(user, token)
    return { ...payload, requiresTwoFactor: false }
  }

  /**
   * Logout admin user
   */
  async logout(): Promise<void> {
    try {
      if (this.adminToken) {
        await apiService.client.post(
          '/admin/logout',
          {},
          {
            headers: {
              Authorization: `Bearer ${this.adminToken}`,
            },
          }
        )
      }
    } catch (error) {
      console.warn('Logout API call failed:', error)
    } finally {
      this.clearAdminData()
    }
  }

  /**
   * Check if user is authenticated as admin
   */
  isAuthenticated(): boolean {
    return !!(
      this.adminToken &&
      this.adminUser &&
      this.adminUser.role === 'admin'
    )
  }

  /**
   * Get current admin user
   */
  getCurrentUser(): AdminUser | null {
    return this.adminUser
  }

  /**
   * Get admin auth token
   */
  getToken(): string | null {
    return this.adminToken
  }

  /**
   * Verify admin token with server
   */
  async verifyToken(): Promise<boolean> {
    try {
      if (!this.adminToken) {
        return false
      }

      const response = await apiService.client.get('/admin/verify', {
        headers: {
          Authorization: `Bearer ${this.adminToken}`,
        },
      })

      return response.data.success
    } catch (error) {
      console.warn('Token verification failed:', error)
      this.clearAdminData()
      return false
    }
  }

  /**
   * Check if admin has specific permission
   */
  hasPermission(permission: string): boolean {
    if (!this.adminUser) {
      return false
    }
    return (
      this.adminUser.permissions.includes(permission) ||
      this.adminUser.permissions.includes('*')
    )
  }

  /**
   * Refresh admin data
   */
  async refreshUser(): Promise<AdminUser | null> {
    try {
      if (!this.adminToken) {
        return null
      }

      const response = await apiService.client.get('/admin/profile', {
        headers: {
          Authorization: `Bearer ${this.adminToken}`,
        },
      })

      if (response.data.success && response.data.data) {
        const user = response.data.data
        this.adminUser = user
        localStorage.setItem('admin_user', JSON.stringify(user))
        return user
      }

      return null
    } catch (error) {
      console.warn('Failed to refresh admin user:', error)
      this.clearAdminData()
      return null
    }
  }
}

// Create and export singleton instance
export const adminAuthService = new AdminAuthService()
